import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from './Modal';

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
`;

const ModalDescription = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  line-height: 1.5;
`;

const FeedbackTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.875rem;
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-size: 0.95rem;
  font-family: var(--font-family-sans);
  outline: none;
  transition: all 0.2s var(--transition-smooth);
  resize: vertical;
  
  &:focus {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.1);
  }
  
  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s var(--transition-smooth);
  font-family: var(--font-family-sans);
  
  &:hover:not(:disabled) {
    background: var(--color-surface);
    border-color: var(--color-border-strong);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-light));
  color: #ffffff;
  border: none;
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s var(--transition-smooth);
  font-family: var(--font-family-sans);
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.4);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface DeclineFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: string) => Promise<void>;
  recommendationTitle?: string;
}

export const DeclineFeedbackModal: React.FC<DeclineFeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  recommendationTitle
}) => {
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(feedback.trim());
      setFeedback('');
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Keep modal open on error so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFeedback('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <ModalTitle id="modal-title">Why are you declining this recommendation?</ModalTitle>
        <ModalDescription>
          {recommendationTitle && (
            <>
              <strong>{recommendationTitle}</strong>
              <br />
            </>
          )}
          Your feedback helps us improve our recommendations. Please share why this recommendation doesn't work for you.
        </ModalDescription>
        <form onSubmit={handleSubmit}>
          <FeedbackTextArea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Enter your feedback here... (e.g., Not relevant for our team, Already tried this, Not applicable to our situation, etc.)"
            disabled={isSubmitting}
            required
            autoFocus
          />
          <ButtonGroup>
            <CancelButton
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </CancelButton>
            <SubmitButton
              type="submit"
              disabled={!feedback.trim() || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </SubmitButton>
          </ButtonGroup>
        </form>
      </ModalContent>
    </Modal>
  );
};

